(() => {
  'use strict';

  const rootSelector = '[data-cwc-guide-article]';
  const faqHeadingPattern = /^(?:faq|frequently asked questions(?:\s*\([^)]*\))?)$/i;
  const desktopQuery = window.matchMedia('(min-width: 761px)');

  const slugify = (value) => {
    const slug = value
      .normalize('NFKD')
      .toLowerCase()
      .replace(/[’']/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return slug || 'section';
  };

  const assignHeadingIds = (headings) => {
    const usedIds = new Set(
      headings.map((heading) => heading.id).filter(Boolean),
    );

    headings.forEach((heading) => {
      if (heading.id) return;

      const baseId = slugify(heading.textContent.trim());
      let candidate = baseId;
      let suffix = 2;

      while (usedIds.has(candidate) || document.getElementById(candidate)) {
        candidate = `${baseId}-${suffix}`;
        suffix += 1;
      }

      heading.id = candidate;
      usedIds.add(candidate);
    });
  };

  const buildTableOfContents = (root, body) => {
    const shell = root.querySelector('[data-cwc-guide-toc-shell]');
    const details = root.querySelector('[data-cwc-guide-toc]');
    const list = root.querySelector('[data-cwc-guide-toc-list]');
    const headings = Array.from(body.querySelectorAll('h2, h3'));

    if (!shell || !details || !list || headings.length === 0) return;

    assignHeadingIds(headings);

    const fragment = document.createDocumentFragment();

    headings.forEach((heading) => {
      const item = document.createElement('li');
      const link = document.createElement('a');

      item.dataset.cwcLevel = heading.tagName.slice(1);
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();
      link.addEventListener('click', () => {
        if (!desktopQuery.matches) details.open = false;
      });

      item.append(link);
      fragment.append(item);
    });

    list.replaceChildren(fragment);
    shell.hidden = false;

    const syncExpandedState = () => {
      details.open = desktopQuery.matches;
    };

    syncExpandedState();

    if (typeof desktopQuery.addEventListener === 'function') {
      desktopQuery.addEventListener('change', syncExpandedState);
    } else if (typeof desktopQuery.addListener === 'function') {
      desktopQuery.addListener(syncExpandedState);
    }
  };

  const enhanceFaq = (body) => {
    const faqHeading = Array.from(body.querySelectorAll('h2, h3')).find(
      (heading) => faqHeadingPattern.test(heading.textContent.trim()),
    );

    if (!faqHeading) return;

    let current = faqHeading.nextElementSibling;

    while (current) {
      if (current.matches('hr, h2, h3')) break;

      const next = current.nextElementSibling;
      const question = current.matches('p') ? current.firstElementChild : null;
      const questionText = question?.textContent.trim() || '';

      if (question?.tagName === 'STRONG' && questionText.endsWith('?')) {
        const details = document.createElement('details');
        const summary = document.createElement('summary');
        const answer = document.createElement('div');

        details.className = 'cwc-guide-faq__item';
        answer.className = 'cwc-guide-faq__answer';

        summary.append(question);
        while (current.firstChild) answer.append(current.firstChild);

        details.append(summary, answer);
        current.replaceWith(details);
      }

      current = next;
    }
  };

  const initializeRoot = (root) => {
    if (root.dataset.cwcGuideInitialized === 'true') return;

    const body = root.querySelector('[data-cwc-guide-body]');
    if (!body) return;

    root.dataset.cwcGuideInitialized = 'true';
    buildTableOfContents(root, body);
    enhanceFaq(body);
  };

  const initializeWithin = (scope) => {
    if (scope.matches?.(rootSelector)) initializeRoot(scope);
    scope.querySelectorAll?.(rootSelector).forEach(initializeRoot);
  };

  const initializeDocument = () => initializeWithin(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeDocument, {
      once: true,
    });
  } else {
    initializeDocument();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeWithin(event.target);
  });
})();
